/**
 * GA4 Event Tracking Utility
 * 
 * Safely tracks events via gtag. Never sends PII.
 * All calls are wrapped to prevent errors if gtag is blocked by ad blockers.
 */

/**
 * Track a GA4 event safely
 * @param {string} eventName - The event name (e.g., 'map_created', 'signup_completed')
 * @param {object} params - Event parameters (no PII allowed)
 */
export function trackEvent(eventName, params = {}) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  } catch (e) {
    // Silently fail - tracking should never break the app
    console.debug('[Analytics] Event tracking failed:', eventName, e);
  }
}

/**
 * Track map creation event
 * @param {'try' | 'app'} source - Where the map was created
 */
export function trackMapCreated(source) {
  trackEvent('map_created', { source });
}

/**
 * Track when user hits the daily trial limit on /try page
 */
export function trackTrialLimitHit() {
  trackEvent('trial_limit_hit');
}

/**
 * Track new account signup (not login)
 * @param {'email' | 'google' | 'facebook'} method - Signup method
 */
export function trackSignupCompleted(method) {
  trackEvent('signup_completed', { method });
}

/**
 * Track user login (existing account)
 * @param {'email' | 'google' | 'facebook'} method - Login method
 */
export function trackLoginCompleted(method) {
  trackEvent('login_completed', { method });
}

/**
 * Track when 7-day free trial starts
 */
export function trackTrialStarted() {
  trackEvent('trial_started');
}

/**
 * Track begin checkout (click on Upgrade/Subscribe)
 * @param {'monthly' | 'annual'} plan - Selected plan
 */
export function trackBeginCheckout(plan) {
  trackEvent('begin_checkout', { plan });
}

/**
 * Track successful purchase after Stripe return
 * Uses sessionStorage to prevent duplicate fires on refresh
 * @param {'monthly' | 'annual'} plan - Purchased plan
 * @param {number} value - Price in USD
 */
export function trackPurchase(plan, value) {
  try {
    const purchaseKey = `purchase_tracked_${Date.now().toString().slice(0, -5)}`; // ~10 second window
    const sessionKey = 'ga4_purchase_session';
    
    // Check if already tracked this session
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const lastTracked = sessionStorage.getItem(sessionKey);
      if (lastTracked) {
        // Already tracked a purchase this session
        return;
      }
      
      // Mark as tracked for this session
      sessionStorage.setItem(sessionKey, Date.now().toString());
    }
    
    trackEvent('purchase', {
      plan,
      value,
      currency: 'USD'
    });
  } catch (e) {
    // Silently fail
    console.debug('[Analytics] Purchase tracking failed:', e);
  }
}

/**
 * Clear the purchase tracking flag (call when starting a new checkout)
 */
export function clearPurchaseFlag() {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem('ga4_purchase_session');
    }
  } catch (e) {
    // Silently fail
  }
}
